import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useAxiosInstance,
} from './utils';

const URL = 'analytics/collection_cohorts';

export type CohortGrouping = 'issue_date' | 'due_date';

type LoadCollectionCohortsVariables = { periodMonths: number; groupBy: CohortGrouping };

const AmountSchema = z.preprocess(val => Number(val), z.number());

const PeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

export const COLLECTION_WINDOWS = [30, 60, 90, 180] as const;

const BucketSchema = z.object({ collected: AmountSchema, billed: AmountSchema });

export type CollectionCohortBucket = z.infer<typeof BucketSchema>;

const BucketsSchema = z.object({
  30: BucketSchema.nullable(),
  60: BucketSchema.nullable(),
  90: BucketSchema.nullable(),
  180: BucketSchema.nullable(),
});

const CohortSchema = z.object({
  period: PeriodSchema,
  invoiceCount: z.number(),
  amount: AmountSchema,
  collected: AmountSchema,
  buckets: BucketsSchema,
});

export type CollectionCohort = z.infer<typeof CohortSchema>;

export const CollectionCohortsSchema = z.object({
  periodMonths: z.number(),
  currency: z.string(),
  cohorts: z.array(CohortSchema),
});

export type CollectionCohorts = z.infer<typeof CollectionCohortsSchema>;

async function loadCollectionCohortsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { periodMonths, groupBy }: LoadCollectionCohortsVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, {
    params: { period_months: periodMonths, group_by: groupBy },
  });

  const parseResult = CollectionCohortsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'collectionCohorts');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadCollectionCohorts({
  periodMonths,
  groupBy,
}: LoadCollectionCohortsVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<CollectionCohorts, ApiError>({
    queryKey: ['collection-cohorts', periodMonths, groupBy],
    queryFn: () => loadCollectionCohortsQueryFn(axiosInstance, { periodMonths, groupBy }),
  });

  return addResourceNameToQueryResult<CollectionCohorts, ApiError, 'collectionCohorts'>(
    'collectionCohorts',
    queryResult,
  );
}
